import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CircleCheck, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function manageEntityAccount(
  id: string,
  requiresEntityAccount: boolean,
  metadata: string | null,
) {
  const params = new URLSearchParams({
    requiresEntityAccount: requiresEntityAccount.toString(),
  })

  if (requiresEntityAccount && metadata) {
    params.append('metadata', metadata)
  } else {
    params.append('metadata', 'null')
  }

  return api.backend.put(
    `/application/manage-account/${id}?${params.toString()}`,
  )
}

export function useManageEntityAccount() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    { id: string; requiresEntityAccount: boolean; metadata: string | null }
  >({
    mutationKey: ['manageEntityAccount'],
    mutationFn: ({ id, requiresEntityAccount, metadata }) =>
      manageEntityAccount(id, requiresEntityAccount, metadata),
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CircleCheck,
          title: t(
            'applications.drawer_manage_entity_account.toast.success.title',
          ),
          description: t(
            'applications.drawer_manage_entity_account.toast.success.message',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t(
            'applications.drawer_manage_entity_account.toast.error.title',
          ),
          description:
            error?.response?.data.friendlyMessage ||
            t('applications.drawer_manage_entity_account.toast.error.message'),
        },
      })
    },
  })

  const handleManageEntityAccount = async (
    id: string,
    requiresEntityAccount: boolean,
    metadata: string | null,
  ) => {
    await mutateAsync({ id, requiresEntityAccount, metadata })
  }

  return {
    handleManageEntityAccount,
    isManagingEntityAccount: isPending,
  }
}
